import { z } from 'zod';
import { queryBoolean } from '../api/query-boolean';

/**
 * The Kids service's public contract.
 *
 * A check-in references a child (and optionally the dropping-off guardian)
 * by Person id alone, resolved through PeopleReadPort at read time — the
 * same rule every dependent module follows. `attendanceRecordId` points at
 * the underlying `AttendanceRecord` KidsService creates by calling
 * AttendanceService directly; Kids owns only what Attendance does not: the
 * pickup security code.
 */

export const checkInChildRequestSchema = z.object({
  childPersonId: z.uuid(),
  /** The adult dropping the child off, when they're a registered Person. */
  guardianDropOffPersonId: z.uuid().nullish(),
  campusId: z.uuid().nullish(),
  note: z.string().trim().max(500).nullish(),
});
export type CheckInChildRequest = z.infer<typeof checkInChildRequestSchema>;

export const checkOutChildRequestSchema = z.object({
  /** The code shown once at check-in. Never logged, never re-issued. */
  code: z.string().trim().min(4).max(10),
  /** Whoever presented the code, when they're a registered Person. */
  pickedUpByPersonId: z.uuid().nullish(),
});
export type CheckOutChildRequest = z.infer<typeof checkOutChildRequestSchema>;

export const listChildCheckInsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  childPersonId: z.uuid().nullish(),
  /** Only check-ins with no `checkedOutAt` yet — the front-desk default view. */
  openOnly: queryBoolean().default(false),
});
export type ListChildCheckInsQuery = z.infer<typeof listChildCheckInsQuerySchema>;

// --- Responses --------------------------------------------------------------

export interface ChildCheckInResponse {
  readonly id: string;
  readonly childPersonId: string;
  readonly childDisplayName: string;
  readonly attendanceRecordId: string;
  readonly campusId: string | null;
  readonly guardianDropOffPersonId: string | null;
  readonly guardianDropOffDisplayName: string | null;
  readonly guardianPickupPersonId: string | null;
  readonly guardianPickupDisplayName: string | null;
  readonly note: string | null;
  readonly checkedInAt: string;
  readonly checkedOutAt: string | null;
}

/**
 * What check-in returns. `securityCode` is the plaintext, present exactly
 * once — the response is the only place it ever exists outside the hash.
 */
export interface ChildCheckInCreatedResponse extends ChildCheckInResponse {
  readonly securityCode: string;
}

export interface GuardianResponse {
  readonly personId: string;
  readonly displayName: string;
}
